<template>
  <div class="password-input-wrapper">
    <div class="password-input-container">
      <input
          v-bind="$attrs"
          :value="isPasswordVisible ? localValue : displayValue"
          @input="handleInput"
          @keydown="handleKeydown"
          :placeholder="placeholder || '请输入密码'"
          type="text"
          autocomplete="new-password"
          class="password-input"
      />
      <!-- 小眼睛按钮 -->
      <button
          type="button"
          class="toggle-password-button"
          @click="togglePasswordVisibility"
          aria-label="切换密码可见性"
      >
        <i :class="isPasswordVisible ? 'icon-eye-open' : 'icon-eye-close'"></i>
        {{ isPasswordVisible ? '隐藏' : '显示' }}
      </button>
    </div>

    <!-- 密码强度指示器 -->
    <div v-if="showStrengthIndicator && localValue.length > 0" class="password-strength-indicator">
      <div class="strength-bars">
        <div
            v-for="(bar, index) in 4"
            :key="index"
            :class="[
            'strength-bar',
            {
              'strength-weak': strengthScore >= 1 && index === 0,
              'strength-medium': strengthScore >= 2 && index <= 1,
              'strength-strong': strengthScore >= 3 && index <= 2,
              'strength-very-strong': strengthScore >= 4 && index <= 3
            }
          ]"
        ></div>
      </div>
      <div class="strength-text">{{ getStrengthText() }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, watch, defineEmits } from 'vue';

// Props
const props = defineProps({
  value: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  showStrengthIndicator: {
    type: Boolean,
    default: true
  },
  inputPrefix: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['update:value', 'strengthChange']);

// 本地值 - 实际密码
const localValue = ref(props.value);
// 显示值 - 星号
const displayValue = ref('');
const strengthScore = ref(0);
// 密码可见性状态
const isPasswordVisible = ref(false);

// 监听props变化
watch(() => props.value, (newValue) => {
  localValue.value = newValue;
  updateDisplayValue();
});

// 更新显示值为星号
const updateDisplayValue = () => {
  displayValue.value = '*'.repeat(localValue.value.length);
};

// 处理输入
const handleInput = (event) => {
  const inputValue = event.target.value;
  const oldLength = localValue.value.length;
  const newLength = inputValue.length;

  // 如果输入长度增加，说明用户输入了新字符
  if (newLength > oldLength) {
    // 获取新输入的字符
    const newChar = inputValue.slice(oldLength);
    // 将新字符添加到实际密码中
    localValue.value += newChar;
    // 更新显示值为星号
    updateDisplayValue();
  } else if (newLength < oldLength) {
    // 如果输入长度减少，说明用户删除了字符
    // 截断实际密码
    localValue.value = localValue.value.slice(0, newLength);
    // 更新显示值为星号
    updateDisplayValue();
  }

  emit('update:value', localValue.value);
  updatePasswordStrength(localValue.value);
};

// 处理按键事件
const handleKeydown = (event) => {
  // 特殊键处理（如删除键、退格键等）由input事件处理
};

// 切换密码可见性
const togglePasswordVisibility = () => {
  isPasswordVisible.value = !isPasswordVisible.value;
};

// 更新密码强度
const updatePasswordStrength = (password) => {
  if (!password) {
    strengthScore.value = 0;
    emit('strengthChange', { score: 0, text: '请输入密码' });
    return;
  }

  // 密码强度检测算法
  let score = 0;

  // 长度检查
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;

  // 包含小写字母
  if (/[a-z]/.test(password)) score++;

  // 包含大写字母
  if (/[A-Z]/.test(password)) score++;

  // 包含数字
  if (/\d/.test(password)) score++;

  // 包含特殊字符
  if (/[^A-Za-z0-9]/.test(password)) score++;

  // 限制分数在1-4之间
  strengthScore.value = Math.min(Math.max(1, Math.floor(score / 1.5)), 4);

  emit('strengthChange', {
    score: strengthScore.value,
    text: getStrengthText()
  });
};

// 获取强度文本
const getStrengthText = () => {
  switch(strengthScore.value) {
    case 1:
      return '弱: 密码较简单';
    case 2:
      return '中: 可以尝试增加复杂度';
    case 3:
      return '强: 密码安全性良好';
    case 4:
      return '非常强: 密码非常安全';
    default:
      return '请输入密码';
  }
};
</script>

<style scoped>
.password-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.password-input {
  width: 100%;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s;
  background-color: #f2f4f7; /* 添加浅色背景 */
}

.password-input:focus {
  border-color: #4096ff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  background-color: #f2f4f7; /* 聚焦时使用白色背景 */
}

.toggle-password-button {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  transition: all 0.3s;
}

.toggle-password-button:hover {
  color: #4096ff;
  background-color: #f0f0f0;
}

/* 简单的眼睛图标样式 */
.icon-eye-open::before {
  content: "👁";
}

.icon-eye-close::before {
  content: "👁️";
  filter: blur(1px);
}

.password-strength-indicator {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.strength-bars {
  display: flex;
  gap: 4px;
  height: 4px;
}

.strength-bar {
  flex: 1;
  background-color: #e8e8e8;
  border-radius: 2px;
  transition: background-color 0.3s ease;
}

.strength-weak {
  background-color: #ff4d4f;
}

.strength-medium {
  background-color: #faad14;
}

.strength-strong {
  background-color: #52c41a;
}

.strength-very-strong {
  background-color: #1890ff;
}

.strength-text {
  font-size: 12px;
  color: #666;
}
</style>